import { Terminal, Equipo } from '../../../../core/context/SessionContext';
import { CartItem } from '../../application/context/CartContext';
import { EnvioNacionalForm } from '../../presentation/components/EnvioNacionalModal/EnvioNacionalModal.types';
import { calcValorDeclaradoGuia } from '../../presentation/components/EnvioNacionalModal/DatosProducto';
import { Producto } from '../../domain/entities/Producto';
import { VentaEmpaqueRecord, KpisEmpaque, Mostrador } from '../../../historial/domain/entities/VentaEmpaqueRecord';

export interface TipoViaResponse {
  id: number | string;
  descripcion: string;
  formato_detallado: boolean;
}

export interface CiudadCOResponse {
  id: string;
  label: string;
  codigoCm: string;
}


// Interfaz para el payload que espera SAP
export interface SAPSalesOrderPayload {
  SalesOrderType: string;
  SalesOrganization: string;
  DistributionChannel: string;
  OrganizationDivision: string;
  SoldToParty: string;
  SalesDistrict: string;
  SalesOffice: string;
  PaymentMethod: string;
  SalesOrderDate: string;
  to_Item: SAPSalesOrderItem[];
  CustomerPaymentTerms: string;
  ShippingCondition: string;
  PurchaseOrderByCustomer: string;
}

export interface SAPSalesOrderItem {
  SalesOrderItem: string;
  Material: string;
  RequestedQuantity: string;
  ProfitCenter: string;
  to_PricingElement: SAPPricingElement[];
  MaterialByCustomer: string;
}

export interface SAPPricingElement {
  ConditionType: string;
  ConditionRateValue: string;
}

export interface ClienteData {
  tipoDocumento: string;
  documento: string;
  nombre: string;
  correo: string;
  bpCliente?: string;
  tipoPersona?: string;
  usoCFDI?: string;
  codigoPostal?: string;
  ciudad?: string;
  departamento?: string;
  codigoCm?: string;
  codigoPais?: number;
  naturalezaReceptor?: string;
  segundoNombre?: string;
  primerApellido?: string;
  segundoApellido?: string;
}

export interface UsoCFDI {
  id: string;
  descripcion: string;
  tipo_persona: string;
}

export interface ResultadoPedido {
  tipo: 'empaque' | 'envio';
  estado: 'COMPLETADO' | 'FALLIDO';
  salesOrder?: string;
  billingDocument?: unknown;
  codigosRemision?: string[];
  error?: string;
}

export interface ResultadoVenta {
  compraId: number;
  estado: 'COMPLETA' | 'PARCIAL' | 'FALLIDA';
  pedidos: ResultadoPedido[];
}

export interface FacturacionResponse {
  success: boolean;
  data: ResultadoVenta;
}

export interface ResumenPedidos {
  salesOrder: string | null;
  codigosRemision: string[];
  tieneEnvioNacional: boolean;
  tieneEmpaque: boolean;
}

export function extraerResumenPedidos(resultado: ResultadoVenta): ResumenPedidos {
  const pedidoEmpaque = resultado.pedidos.find(p => p.tipo === 'empaque');
  const pedidoEnvio = resultado.pedidos.find(p => p.tipo === 'envio');
  return {
    salesOrder: pedidoEmpaque?.salesOrder ?? null,
    codigosRemision: pedidoEnvio?.codigosRemision ?? [],
    tieneEnvioNacional: !!pedidoEnvio,
    tieneEmpaque: !!pedidoEmpaque,
  };
}

export interface ClienteBusquedaResponse {
  bpCliente: string;
  nombre: string;
}

export interface HistorialFiltros {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  terminal?: number;
  equipo?: number;
}

export interface EmpaqueDetalleFiltros {
  startDate: string;
  endDate: string;
  terminal?: string;
  mostrador?: string;
}

export class VentaApiService {
  private static readonly BASE_URL = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:4000';

  private static async fetchConAuth(url: string, options: RequestInit, pais?: string): Promise<Response> {
    const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };
    if (pais) headers['X-Pais'] = pais;
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) {
      globalThis.dispatchEvent(new CustomEvent('auth:sesion-expirada'));
      throw new Error('Sesión expirada. Por favor inicia sesión nuevamente.');
    }
    return response;
  }

  static async crearFacturaSAP(
    terminal: Terminal, 
    equipo: Equipo, 
    carrito: CartItem[], 
    metodoPago: string, 
    clienteData: ClienteData,
    accessToken: string
  ): Promise<FacturacionResponse> {
    const API_URL = `${this.BASE_URL}/ventas`;
    
    const payload = {
      terminal,
      equipo,
      carrito: carrito.map(({ envioNacionalForm: _form, ...item }) => item),
      metodoPago,
      clienteData
    };

    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(payload)
    }, terminal.pais);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.details ?? errorData.error ?? 'Error desconocido en el backend');
    }

    return response.json() as Promise<FacturacionResponse>;
  }

  static async obtenerHistorialBD(
    accessToken: string,
    pais: string,
    page: number = 1,
    limit: number = 10,
    filtros: HistorialFiltros = {}
  ): Promise<{ data: any[], total: number }> {
    const { startDate, endDate, terminal, equipo } = filtros;
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (terminal !== undefined) params.append('terminal', terminal.toString());
    if (equipo !== undefined) params.append('equipo', equipo.toString());

    const API_URL = `${this.BASE_URL}/ventas?${params.toString()}`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }, pais);
    
    if (!response.ok) {
      throw new Error('Error al obtener el historial de la base de datos');
    }
    
    const json = await response.json();
    return { data: json.data, total: json.total };
  }

  static async obtenerProductosBD(accessToken: string, pais: string): Promise<Producto[]> {
    const API_URL = `${this.BASE_URL}/productos`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }, pais);
    
    if (!response.ok) {
      throw new Error('Error al obtener los productos de la base de datos');
    }
    
    const json = await response.json();
    return json.data;
  }

  static async obtenerTerminalesBD(accessToken: string, pais?: string): Promise<Terminal[]> {
    const API_URL = `${this.BASE_URL}/terminales`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }, pais);
    
    if (!response.ok) {
      throw new Error('Error al obtener las terminales de la base de datos');
    }
    
    const json = await response.json();
    return json.data;
  }

  static async obtenerEquiposBD(codigoTerminal: number, accessToken: string, pais?: string): Promise<Equipo[]> {
    const API_URL = `${this.BASE_URL}/equipos/${codigoTerminal}`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }, pais);
    
    if (!response.ok) {
      throw new Error('Error al obtener los equipos de la base de datos');
    }
    
    const json = await response.json();
    return json.data;
  }

  static async obtenerMetodosPagoBD(accessToken: string, pais: string): Promise<any[]> {
    const API_URL = `${this.BASE_URL}/metodos-pago`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }, pais);
    
    if (!response.ok) {
      throw new Error('Error al obtener los métodos de pago de la base de datos');
    }
    
    const json = await response.json();
    return json.data;
  }

  static async buscarClientePorDocumento(
    documento: string,
    accessToken: string,
    pais: string
  ): Promise<ClienteBusquedaResponse> {
    const API_URL = `${this.BASE_URL}/ventas/consultar-BP/${encodeURIComponent(documento)}`;

    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }, pais);

    if (!response.ok) {
      throw new Error('Error al buscar el cliente. Intenta de nuevo.');
    }

    const json = await response.json();
    return {
      bpCliente: json.data?.bpCliente ?? '',
      nombre: json.data?.nombre ?? ''
    };
  }

  static async obtenerUsosCFDI(tipoPersona: string, accessToken: string, pais: string): Promise<UsoCFDI[]> {
    const params = new URLSearchParams({ tipo_persona: tipoPersona });
    const API_URL = `${this.BASE_URL}/ventas/usos-cfdi?${params.toString()}`;

    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }, pais);

    if (!response.ok) {
      throw new Error('Error al obtener los usos del CFDI.');
    }

    const json = await response.json();
    return json.data as UsoCFDI[];
  }

  static async crearClienteFacturacion(clienteData: ClienteData, accessToken: string, pais: string): Promise<string> {
    const API_URL = `${this.BASE_URL}/ventas/crear-cliente-facturacion`;
    const payload = {
      tipoDocumento: clienteData.tipoDocumento,
      documento: clienteData.documento,
      nombre: clienteData.nombre,
      correo: clienteData.correo,
      tipoPersona: clienteData.tipoPersona,
      codigoPostal: clienteData.codigoPostal,
      departamento: clienteData.departamento,
      codigo_cm: clienteData.codigoCm,
      codigoPais: clienteData.codigoPais
    };
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      body: JSON.stringify(payload)
    }, pais);
    if (!response.ok) {
      const errorJson = await response.json().catch(() => null);
      const mensaje = errorJson?.message ?? errorJson?.error ?? 'Error al crear el cliente de facturación.';
      throw new Error(mensaje);
    }
    const json = await response.json();
    return json.data?.bpCliente ?? '';
  }

  static async consultarCodigoPostal(codigoPostal: string, accessToken: string, pais: string): Promise<{ ciudad: string; poblacion: string; regionCodigo: string; codigoCm: string; pais: string; codigoCiudadDane: string }> {
    const API_URL = `${this.BASE_URL}/ventas/consultar-codigo-postal/${encodeURIComponent(codigoPostal)}`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${accessToken}` }
    }, pais);

    if (!response.ok) {
      throw new Error('Código postal no válido');
    }

    const json = await response.json();
    return {
      ciudad: json.data?.municipio ?? '',
      poblacion: json.data?.poblacion ?? '',
      regionCodigo: json.data?.region?.codigo ?? '',
      codigoCm: json.data?.codigo_cm ?? '',
      pais: json.data?.pais ?? '',
      codigoCiudadDane: json.data?.codigo ?? '',
    };
  }

  static async cotizarEnvioNacional(form: EnvioNacionalForm, accessToken: string, pais: string): Promise<number> {
    const API_URL = `${this.BASE_URL}/cotizacion`;

    const detalle = form.producto.tipo === 'paquete'
      ? form.producto.paquetes.map(p => ({
          alto:     Number.parseFloat(p.alto),
          ancho:    Number.parseFloat(p.ancho),
          largo:    Number.parseFloat(p.largo),
          pesoReal: Number.parseFloat(p.peso),
          unidades: p.cantidad,
        }))
      : [{
          alto:     1,
          ancho:    1,
          largo:    1,
          pesoReal: Number.parseInt(form.producto.pesoDocumento, 10),
          unidades: 1,
        }];

    const esColombia = pais === 'CO';
    const valoracion = esColombia && form.producto.tipo === 'paquete'
      ? calcValorDeclaradoGuia(form.producto.paquetes)
      : Number.parseFloat(form.producto.valorDeclarado);

    const payload = {
      ciudadOrigen:      Number.parseInt(form.remitente.codigoCm, 10) || 208,
      ciudadDestino:     Number.parseInt(form.destinatario.codigoCm, 10) || 306,
      detalle,
      guardarCotizacion: true,
      valoracion,
    };

    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    }, pais);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message ?? errorData?.error ?? 'Error al cotizar el envío');
    }

    const json = await response.json();
    const inconsistencias: string[] = json.data?.inconsistencias ?? [];
    if (inconsistencias.length > 0) {
      throw new Error(inconsistencias.join(' / '));
    }
    const cotizacion = json.data?.cotizacion;
    const valorEnvio = cotizacion?.valorEnvio;
    if (typeof valorEnvio !== 'number' || valorEnvio <= 0) {
      throw new Error('El envío cotizó en $0 y no se puede agregar al carrito. Verifica origen, destino y dimensiones.');
    }
    return valorEnvio;
  }

  static async imprimirEtiquetas(
    guias: string[],
    accessToken: string,
    pais: string
  ): Promise<{ resultado: string; pdfBase64: string }[]> {
    const API_URL = `${this.BASE_URL}/guias/imprimir-etiquetas`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ guias }),
    }, pais);
    if (!response.ok) {
      throw new Error('Error al obtener las etiquetas de envío');
    }
    const json = await response.json();
    return json.data;
  }

  static async obtenerKpisEmpaque(
    accessToken: string,
    pais: string,
    startDate: string,
    endDate: string
  ): Promise<KpisEmpaque> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await VentaApiService.fetchConAuth(
      `${this.BASE_URL}/ventas/empaque/kpis?${params}`,
      { method: 'GET', headers: { Authorization: `Bearer ${accessToken}` } },
      pais
    );
    if (!response.ok) throw new Error('Error al obtener los KPIs de empaque');
    const json = await response.json();
    return json.data as KpisEmpaque;
  }

  static async obtenerDetalleEmpaque(
    accessToken: string,
    pais: string,
    page: number,
    limit: number,
    filtros: EmpaqueDetalleFiltros
  ): Promise<{ data: VentaEmpaqueRecord[]; total: number }> {
    const { startDate, endDate, terminal, mostrador } = filtros;
    const params = new URLSearchParams({ startDate, endDate, page: String(page), limit: String(limit) });
    if (terminal) params.append('terminal', terminal);
    if (mostrador) params.append('mostrador', mostrador);
    const response = await VentaApiService.fetchConAuth(
      `${this.BASE_URL}/ventas/empaque/detalle?${params}`,
      { method: 'GET', headers: { Authorization: `Bearer ${accessToken}` } },
      pais
    );
    if (!response.ok) throw new Error('Error al obtener el detalle de empaque');
    const json = await response.json();
    return { data: json.data, total: json.total };
  }

  static async exportarDetalleEmpaque(
    accessToken: string,
    pais: string,
    startDate: string,
    endDate: string,
    terminal?: string,
    mostrador?: string
  ): Promise<VentaEmpaqueRecord[]> {
    const params = new URLSearchParams({ startDate, endDate });
    if (terminal) params.append('terminal', terminal);
    if (mostrador) params.append('mostrador', mostrador);
    const response = await VentaApiService.fetchConAuth(
      `${this.BASE_URL}/ventas/empaque/detalle/exportar?${params}`,
      { method: 'GET', headers: { Authorization: `Bearer ${accessToken}` } },
      pais
    );
    if (!response.ok) throw new Error('Error al obtener los datos para exportar');
    const json = await response.json();
    return json.data as VentaEmpaqueRecord[];
  }

  /**
   * Retorna los tipos de vía disponibles para Colombia.
   * El backend hace proxy hacia el servicio de ruteo de terminales.
   */
  static async obtenerTiposViaCO(accessToken: string): Promise<TipoViaResponse[]> {
    const API_URL = `${this.BASE_URL}/ventas/tipos-via`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${accessToken}` },
    }, 'CO');
    if (!response.ok) throw new Error('Error al obtener los tipos de vía');
    const json = await response.json();
    return json.data as TipoViaResponse[];
  }

  /**
   * Retorna las ciudades de Colombia para el autocomplete de ciudad.
   * El backend hace proxy hacia maestros-services/poblaciones/pais/170.
   */
  static async obtenerCiudadesCO(accessToken: string): Promise<CiudadCOResponse[]> {
    const API_URL = `${this.BASE_URL}/ventas/ciudades-co`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${accessToken}` },
    }, 'CO');
    if (!response.ok) throw new Error('Error al obtener las ciudades de Colombia');
    const json = await response.json();
    return json.data as CiudadCOResponse[];
  }


  static async obtenerMostradoresBD(codigoTerminal: number, accessToken: string, pais: string): Promise<Mostrador[]> {
    const params = new URLSearchParams({ codigoTerminal: String(codigoTerminal) });
    const response = await VentaApiService.fetchConAuth(
      `${this.BASE_URL}/mostradores?${params}`,
      { method: 'GET', headers: { Authorization: `Bearer ${accessToken}` } },
      pais
    );
    if (!response.ok) throw new Error('Error al obtener los mostradores');
    const json = await response.json();
    return json.data as Mostrador[];
  }

  static async obtenerDepartamentosCO(accessToken: string): Promise<{ codigo: string; nombre: string }[]> {
    const API_URL = `${this.BASE_URL}/ventas/poblaciones?tipo=dpto`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${accessToken}` }
    }, 'CO');
    if (!response.ok) throw new Error('Error al obtener los departamentos');
    const json = await response.json();
    return json.data;
  }

  static async obtenerCiudadesPorDepartamentoCO(codigoDpto: string, accessToken: string): Promise<string[]> {
    const params = new URLSearchParams({ tipo: 'ciudad', dpto: codigoDpto });
    const API_URL = `${this.BASE_URL}/ventas/poblaciones?${params.toString()}`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${accessToken}` }
    }, 'CO');
    if (!response.ok) throw new Error('Error al obtener las ciudades');
    const json = await response.json();
    return json.data;
  }

  /**
   * Usa compraId como identificador universal (siempre existe, a diferencia
   * de idPedidoSAP, que es null para compras 100% envío). Ver
   * CONTRATO-BACKEND-SPEC-001-ventas.md sección 3bis (TASK-016, CERTIFICADO).
   */
  static async obtenerTiqueteBD(compraId: number, accessToken: string, pais: string, codigoTerminal?: number): Promise<any> {
    const params = codigoTerminal === undefined ? '' : `?codigoTerminal=${codigoTerminal}`;
    const API_URL = `${this.BASE_URL}/ventas/compra/${compraId}/tiquete${params}`;
    const response = await VentaApiService.fetchConAuth(API_URL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }, pais);
    
    if (!response.ok) {
      throw new Error('Error al obtener la información del tiquete');
    }
    
    const json = await response.json();
    return VentaApiService.consolidarTiquete(json.data);
  }

  /**
   * El backend devuelve { compraId, estado, pedidos: [...] } (1 o 2 pedidos:
   * empaque y/o envío). Esta función lo aplana a un shape retrocompatible con
   * `VentaRecord` (el que ya consume generarTiquetePDF), sumando los montos de
   * todos los pedidos y uniendo items/guías, y además expone los datos crudos
   * por pedido (`pedidos`) para quien necesite detalle de fallos parciales.
   * `idPedidoSAP` puede resultar `null` (compra 100% envío) — quien consuma
   * este objeto debe manejar ese caso (mostrar guía o compraId en su lugar).
   */
  private static consolidarTiquete(data: any): any {
    if (!data?.pedidos?.length) return data;

    const pedidos = data.pedidos as any[];
    const pedidoConIdSAP = pedidos.find(p => p.idPedidoSAP) ?? pedidos[0];
    const items = pedidos.flatMap(p => p.items ?? []);
    const guias = pedidos.flatMap(p => p.guias ?? []);

    // `subtotal`/`impuestos`/`total` llegan como string (columnas `numeric`
    // de Postgres, el driver `pg` no las convierte a número); sumarlas con
    // `+` sin convertir produce concatenación de strings (y luego NaN al
    // convertir el resultado final). `baseGravable`/`ivaEnvios`/etc. ya
    // llegan como number (se calculan en JS en el backend), pero Number()
    // sobre un number no tiene efecto, así que es seguro aplicarlo siempre.
    const sumar = (campo: string) => pedidos.reduce((acc, p) => acc + Number(p[campo] ?? 0), 0);
    const tieneDesgloseMX = pedidos.some(p => p.ivaEnvios !== undefined);

    return {
      compraId: data.compraId,
      estadoCompra: data.estado,
      pedidos,
      idPedidoSAP: pedidoConIdSAP?.idPedidoSAP ?? null,
      fecha: pedidoConIdSAP?.fecha,
      cliente: pedidoConIdSAP?.cliente,
      metodoPago: pedidoConIdSAP?.metodoPago,
      terminal: pedidoConIdSAP?.terminal,
      codigoTerminal: pedidoConIdSAP?.codigoTerminal,
      equipo: pedidoConIdSAP?.equipo,
      pais: pedidoConIdSAP?.pais,
      items,
      guias,
      subtotal: sumar('subtotal'),
      impuestos: sumar('impuestos'),
      total: sumar('total'),
      ...(tieneDesgloseMX && {
        baseGravable: sumar('baseGravable'),
        ivaEnvios: sumar('ivaEnvios'),
        ivaEmpaques: sumar('ivaEmpaques'),
        totalImpuestos: sumar('totalImpuestos'),
      }),
    };
  }
}
